// Helper to get config from localStorage first, then fall back to process.env
const getFromLocalStorage = (key: string): string | null => {
  if (typeof window !== 'undefined' && window.localStorage) {
    return localStorage.getItem(key);
  }
  return null;
};

export const RUNTIME_ENV =
  getFromLocalStorage('RUNTIME_ENV') || process.env.RUNTIME_ENV || 'production';
export const OPAL_BACKEND_URL =
  getFromLocalStorage('OPAL_BACKEND_URL') ||
  process.env.OPAL_BACKEND_URL ||
  'https://opal-backend.optimizely.com';

export const OPAL_URL =
  getFromLocalStorage('OPAL_URL') || process.env.OPAL_URL || 'https://opal.optimizely.com';
export const OKTA_LOGOUT_URL = `${OPAL_URL}/logout`;
export const HYPATIA_URL =
  getFromLocalStorage('HYPATIA_URL') ||
  process.env.HYPATIA_URL ||
  'https://opal.optimizely.com/hypatia';
// Admin dashboards app (sibling frontend). Used to link publishers to the dyson
// rollout job view after publishing a system/library agent. The rollout job is
// always created in the US authoring region, so the US dashboards host is the
// correct default; localdev/integration override via their .env files.
export const DASHBOARDS_URL =
  getFromLocalStorage('DASHBOARDS_URL') ||
  process.env.DASHBOARDS_URL ||
  'https://dashboards.opal.optimizely.com';
export const INSTRUCTIONS_URL = `${OPAL_URL}/instructions-service`;
export const SEGMENT_WRITE_KEY =
  getFromLocalStorage('SEGMENT_WRITE_KEY') || process.env.SEGMENT_WRITE_KEY || '';
// Fullstory org id is environment-specific (separate org per localdev/integration/
// production) so each environment records to its own Fullstory org. An empty value
// is a no-op: Fullstory is never initialized, which keeps the app safe before keys
// are provisioned and keeps localdev off by default.
export const FULLSTORY_ORG_ID =
  getFromLocalStorage('FULLSTORY_ORG_ID') || process.env.FULLSTORY_ORG_ID || '';
// Deliberately NOT overridable from localStorage: pointing the app at a
// different FX datafile would turn every flag back on, which defeats the
// bug-bounty flag suppression in `contexts/flagSuppression.ts`. The frontend is
// not a security boundary either way -- see
// docs/tech-spec/bug-crowd-feature-flag-suppression.md sections 3.3 and 5.
export const OPTIMIZELY_SDK_KEY = process.env.OPTIMIZELY_SDK_KEY || '';
// Bug-bounty org/instance ids whose FX flags are all forced off. CSV; empty
// disables the guard. Baked at build time, so it must be declared in every
// .env.<environment> file or the bundler leaves a bare `process` reference.
// See docs/tech-spec/bug-crowd-feature-flag-suppression.md.
export const FEATURE_FLAGS_DISABLED_IDS = process.env.FEATURE_FLAGS_DISABLED_IDS || '';
export const OPAL_SYSTEM_INSTRUCTION_MANAGER_FEATURE_FLAG = 'system_instructions';
export const HOME_URL =
  RUNTIME_ENV === 'production' ? 'https://home.optimizely.com' : 'https://prep.home.optimizely.com';
export const EMAIL_DOMAIN_SUFFIX_US = '.opal.optimizely.com';
export const EMAIL_DOMAIN_SUFFIX_EU = '.eu.opal.optimizely.com';

export const EMAIL_DOMAIN_SUFFIX =
  getFromLocalStorage('EMAIL_DOMAIN_SUFFIX') ||
  process.env.EMAIL_DOMAIN_SUFFIX ||
  EMAIL_DOMAIN_SUFFIX_US;

// Non-EU falls through to EMAIL_DOMAIN_SUFFIX, which honours the env var and
// the localStorage override, rather than to the hardcoded US constant.
//
// Returning EMAIL_DOMAIN_SUFFIX_US here ignored EMAIL_DOMAIN_SUFFIX entirely,
// so localdev and integration — both configured for .inte.opal.optimizely.com —
// built domains on the production apex instead. Those cannot be delegated from
// the integration AWS account, which hosts inte.opal.optimizely.com but not
// opal.optimizely.com, so provisioning failed on a domain the UI had offered.
//
// Production is unaffected: .env.production sets the same value the constant
// held, and EMAIL_DOMAIN_SUFFIX defaults to it when nothing is configured.
export const getEmailDomainSuffix = (hostedRegion?: string): string => {
  return hostedRegion === 'EU' ? EMAIL_DOMAIN_SUFFIX_EU : EMAIL_DOMAIN_SUFFIX;
};
export const WS_GATEWAY_URL = process.env.WS_GATEWAY_URL || 'https://ws.opal.optimizely.com';
export const MESSAGING_SERVICE_URL =
  getFromLocalStorage('MESSAGING_SERVICE_URL') ||
  process.env.MESSAGING_SERVICE_URL ||
  `${OPAL_URL}/messaging-service`;
export const SOCKETIO_PATH = '/opal-socket/opal-app-socket.io';
export const MCP_MANAGER_URL =
  getFromLocalStorage('MCP_MANAGER_URL') ||
  process.env.MCP_MANAGER_URL ||
  `${OPAL_URL}/mcp-manager`;
export const MCP_DOMAIN =
  getFromLocalStorage('MCP_DOMAIN') || process.env.MCP_DOMAIN || 'mcp.opal.optimizely.com';
export const A2A_DOMAIN =
  getFromLocalStorage('A2A_DOMAIN') || process.env.A2A_DOMAIN || 'a2a-server.opal.optimizely.com';
export const A2A_OAUTH_METADATA_URL =
  getFromLocalStorage('A2A_OAUTH_METADATA_URL') || process.env.A2A_OAUTH_METADATA_URL || '';
export const ODP_BASE_URL =
  getFromLocalStorage('ODP_BASE_URL') || process.env.ODP_BASE_URL || 'https://app.zaius.com';
export const CMP_BASE_URL =
  getFromLocalStorage('CMP_BASE_URL') ||
  process.env.CMP_BASE_URL ||
  (RUNTIME_ENV === 'production'
    ? 'https://cmp.optimizely.com/'
    : 'https://cmp-dev.optimizely.com/');
